import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComprehensiveRecordsTable = ({ 
  records = [], 
  loading = false, 
  onViewRecord, 
  onExportResults,
  pagination = { page: 1, totalPages: 0, total: 0 },
  onPageChange
}) => {
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({
    key: 'arrest_datetime',
    direction: 'desc'
  });
  const [showDamianMessage, setShowDamianMessage] = useState(false);

  // Show Damian's message when results are displayed
  useEffect(() => {
    if (records?.length > 0) {
      setShowDamianMessage(true);
    }
  }, [records]);

  const handleSelectRecord = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected?.has(recordId)) {
      newSelected?.delete(recordId);
    } else {
      newSelected?.add(recordId);
    }
    setSelectedRecords(newSelected);
    
    // Hide Damian's message after user makes selections
    if (newSelected?.size > 0) {
      setShowDamianMessage(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedRecords?.size === records?.length) {
      setSelectedRecords(new Set());
      setShowDamianMessage(true);
    } else {
      setSelectedRecords(new Set(records?.map(record => record?.arrest_id)));
      setShowDamianMessage(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleExport = () => {
    const recordIds = selectedRecords?.size > 0 
      ? Array.from(selectedRecords) 
      : records?.map(record => record?.arrest_id);
    onExportResults?.(recordIds);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString)?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString)?.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'IN_CUSTODY': { color: 'bg-red-100 text-red-800', label: 'In Custody' },
      'RELEASED': { color: 'bg-green-100 text-green-800', label: 'Released' },
      'DISMISSED': { color: 'bg-blue-100 text-blue-800', label: 'Dismissed' },
      'UNKNOWN': { color: 'bg-gray-100 text-gray-800', label: 'Unknown' }
    };

    const config = statusConfig?.[status] || statusConfig?.['UNKNOWN'];
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'N/A';
    return text?.length > maxLength ? text?.substring(0, maxLength) + '...' : text;
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 text-left font-medium text-muted-foreground hover:text-foreground"
    >
      <span>{children}</span>
      {sortConfig?.key === column && (
        <Icon 
          name={sortConfig?.direction === 'asc' ? "ChevronUp" : "ChevronDown"} 
          size={14} 
        />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="Loader" size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Searching records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!records?.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Records Found</h3>
          <p className="text-muted-foreground mb-4">
            No inmate records match your search criteria. Try adjusting your filters.
          </p>
          {/* Damian's help message for no results */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon name="MessageCircle" size={16} className="text-blue-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-blue-900">Damian (Chat Bot)</p>
                <p className="text-sm text-blue-700 mt-1">
                  I can help you search with different information. Try using partial names, different spellings, or contact me in the chat for assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Damian's Chat Message */}
      {showDamianMessage && (
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <div className="flex items-start space-x-3 max-w-4xl mx-auto">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Icon name="MessageCircle" size={16} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900">Damian (Chat Bot)</p>
              <p className="text-sm text-blue-700 mt-1">
                If you see the person, check the box next to the name. If you don't see the person, I can keep searching if you have different information.
              </p>
            </div>
            <button
              onClick={() => setShowDamianMessage(false)}
              className="text-blue-400 hover:text-blue-600"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Table Header */}
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Search Results</h3>
            <p className="text-sm text-muted-foreground">
              {pagination?.total?.toLocaleString()} records found
              {selectedRecords?.size > 0 && ` • ${selectedRecords?.size} selected`}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {selectedRecords?.size > 0 && (
              <div className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium">
                {selectedRecords?.size} selected
              </div>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={records?.length === 0}
              className="flex items-center space-x-2"
            >
              <Icon name="Download" size={16} />
              <span>Export</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="w-12 px-6 py-3">
                <input
                  type="checkbox"
                  checked={selectedRecords?.size === records?.length && records?.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border text-primary focus:ring-primary"
                  title="Select all records"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="first_name">Name</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="date_of_birth">DOB</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="arrest_datetime">Arrest Date</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="facility_id">Facility</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="agency_name">Agency</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Charges
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="total_bail">Bail</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <SortButton column="status">Status</SortButton>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {records?.map((record) => (
              <tr key={record?.arrest_id} className="hover:bg-muted/50">
                <td className="w-12 px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedRecords?.has(record?.arrest_id)}
                    onChange={() => handleSelectRecord(record?.arrest_id)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {record?.last_name}, {record?.first_name} {record?.middle_name}
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {formatDate(record?.date_of_birth)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {formatDateTime(record?.arrest_datetime)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">
                    {record?.facility_id || 'N/A'}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">
                    {truncateText(record?.agency_name, 20)}
                  </div>
                </td>
                
                <td className="px-6 py-4">
                  <div className="text-sm text-foreground max-w-xs">
                    {truncateText(record?.charges, 40)}
                  </div>
                  {record?.warrants && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Warrants: {truncateText(record?.warrants, 30)}
                    </div>
                  )}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {record?.total_bail ? `$${record?.total_bail?.toLocaleString()}` : 'N/A'}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(record?.status)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => onViewRecord?.(record)}
                    className="flex items-center space-x-1 text-primary hover:text-primary/80"
                  >
                    <Icon name="Eye" size={16} />
                    <span>View</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination?.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((pagination?.page - 1) * 25) + 1} to {Math.min(pagination?.page * 25, pagination?.total)} of{' '}
              {pagination?.total?.toLocaleString()} results
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(pagination?.page - 1)}
                disabled={pagination?.page <= 1}
                className="flex items-center space-x-1"
              >
                <Icon name="ChevronLeft" size={16} />
                <span>Previous</span>
              </Button>
              
              <span className="text-sm text-muted-foreground">
                Page {pagination?.page} of {pagination?.totalPages}
              </span>
              
              <Button
                variant="outline" 
                size="sm"
                onClick={() => onPageChange?.(pagination?.page + 1)}
                disabled={pagination?.page >= pagination?.totalPages}
                className="flex items-center space-x-1"
              >
                <span>Next</span>
                <Icon name="ChevronRight" size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveRecordsTable;